import { useEffect, useRef } from "react";

const srOnly = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

/**
 * Opacity reveal driven by scroll position, replicating the original site's
 * GSAP SplitText + ScrollTrigger scrub effect (dim -> full as the text
 * scrolls through the viewport), without pulling in GSAP.
 *
 * - unit: "char" reveals letter by letter, "word" word by word
 *   (word mode mirrors GSAP's `stagger: 0.05` over a 0.5s tween).
 * - startAt: reveal begins when the text's top is this fraction down the viewport.
 * - endAt / endAnchor: reveal completes when the point `endAnchor` of the way
 *   down the text (1 = bottom, 0.5 = center) reaches `endAt` of the viewport.
 * - children render after the animated text, always fully visible.
 */
export default function ScrollRevealText({
  text,
  className,
  as: Tag = "div",
  unit = "char",
  minOpacity = 0.15,
  startAt = 0.85,
  endAt = 0.35,
  endAnchor = 1,
  children,
}) {
  const containerRef = useRef(null);
  const partsRef = useRef([]);
  const lastRef = useRef([]);

  useEffect(() => {
    partsRef.current = Array.from(
      containerRef.current.querySelectorAll("[data-reveal-part]")
    );
    lastRef.current = [];

    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) {
      partsRef.current.forEach((p) => (p.style.opacity = "1"));
      return;
    }

    let frame = null;

    const update = () => {
      frame = null;
      const el = containerRef.current;
      const parts = partsRef.current;
      if (!el || parts.length === 0) return;

      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;

      const startY = vh * startAt;
      const endY = vh * endAt - rect.height * endAnchor;
      const progress = Math.min(
        1,
        Math.max(0, (startY - rect.top) / (startY - endY || 1))
      );

      const n = parts.length;
      // [start, end] slice of the progress range each part animates over
      let span, step;
      if (unit === "word") {
        const total = 0.5 + 0.05 * (n - 1);
        span = 0.5 / total;
        step = 0.05 / total;
      } else {
        span = 1 / Math.max(1, n * 0.6);
        step = (1 - span) / n;
      }

      // only touch the parts whose opacity actually changed
      const last = lastRef.current;
      for (let i = 0; i < n; i++) {
        const partStart = i * step;
        const local = Math.min(1, Math.max(0, (progress - partStart) / span));
        const o = Math.round((minOpacity + local * (1 - minOpacity)) * 1000) / 1000;
        if (last[i] === o) continue;
        last[i] = o;
        parts[i].style.opacity = String(o);
      }
    };

    const request = () => {
      if (frame == null) frame = requestAnimationFrame(update);
    };
    // far from the viewport a scroll can't change anything (the layout read costs a frame, though)
    let near = true;
    const onScroll = () => near && request();

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", request);
    // on the way in or out, one pass settles the final opacities
    const io = new IntersectionObserver(
      ([entry]) => {
        near = entry.isIntersecting;
        request();
      },
      { rootMargin: "150px 0px" }
    );
    io.observe(containerRef.current);

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", request);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [text, unit, minOpacity, startAt, endAt, endAnchor]);

  const partStyle = { opacity: minOpacity, display: "inline-block" };

  let index = 0;
  const wordNodes = text.split(" ").map((word, wi) => {
    if (unit === "word") {
      return (
        <span key={wi} data-reveal-part aria-hidden="true" style={partStyle}>
          {word}
        </span>
      );
    }
    return (
      <span key={wi} aria-hidden="true" style={{ whiteSpace: "nowrap" }}>
        {word.split("").map((ch) => (
          <span key={index++} data-reveal-part style={partStyle}>
            {ch}
          </span>
        ))}
      </span>
    );
  });

  return (
    <Tag ref={containerRef} className={className}>
      <span style={srOnly}>{text}</span>
      {wordNodes.reduce((acc, el, i) => {
        if (i > 0) acc.push(" ");
        acc.push(el);
        return acc;
      }, [])}
      {children}
    </Tag>
  );
}
